import { Injectable, inject, Injector } from "@angular/core";
// import { ApiService } from '@core/services/api/api.service';
@Injectable({ providedIn: "root" })
/**
 * Servicio encargado de gestionar el almacenamiento en la sesión del navegador (sessionStorage).
 * Proporciona métodos para guardar, recuperar, actualizar y eliminar datos, 
 * encargándose de codificar en Base64 y serializar objetos JSON según sea necesario.
 */
export class manageStorageService {
  /**
   * Inicializa el servicio de almacenamiento.
   * @param {Injector} _injector - Inyector de dependencias de Angular.
   */
  constructor(
    private _injector: Injector,
    // private _serviceUtil: ApiService
  ) { }

  /**
   * Separador interno utilizado para almacenar y recuperar múltiples elementos como una cadena unida.
   * @type {string}
   */
  private chartSeparator = "/-/";

  /**
   * Elimina un elemento específico o todos los elementos del `sessionStorage`,
   * a excepción de la información de geolocalización ('geoLocationInfo').
   * 
   * @param {string} item - La clave del elemento a eliminar. Usa "all" para vaciar todo el almacenamiento permitido.
   * @returns {void} No retorna ningún valor.
   */
  public clearStorage(item: string): void {
    if (item === "all") {
      var i = sessionStorage.length;
      while (i--) {
        var key: string | null = sessionStorage.key(i);
        if (key && key !== "geoLocationInfo") { sessionStorage.removeItem(key); }
      }
    } else {
      if (sessionStorage[item]) { sessionStorage.removeItem(item); }
    }
  }

  /**
   * Verifica si una cadena de texto dada está codificada en Base64 de forma válida.
   * 
   * @private
   * @param {string | null} str - La cadena de texto a evaluar.
   * @returns {boolean} `true` si la cadena es Base64 válida, `false` en caso contrario o si es `null`.
   */
   private isBase64(str: string | null): boolean {
    if (!str || typeof str !== 'string') {
      return false;
    }
    
    try {
      return btoa(atob(str)) === str;
    } catch {
      return false;
    }
  }

  /**
   * Verifica si una cadena de texto dada puede ser parseada como un JSON válido.
   * 
   * @private
   * @param {string | null} str - La cadena de texto a evaluar.
   * @returns {boolean} `true` si la cadena se puede parsear como JSON válido, `false` en caso contrario o si es `null`.
   */
  private isJson(str: string | null): boolean {
    if (!str || typeof str !== 'string') {
      return false;
    }
    
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Obtiene una propiedad almacenada en el `sessionStorage` usando su clave.
   * Decodifica automáticamente la cadena de Base64 y la parsea a JSON si es posible.
   * 
   * @param {string} propertyKey - El nombre de la clave a obtener en el almacenamiento.
   * @returns {T | string | null} El valor recuperado en su tipo original (o un string), o `null` si no existe o hubo un error.
   */
  public getPropertie<T = any>(propertyKey: string): T | string | null {
    try {
      // Verificar si la propiedad existe
      const rawValue = sessionStorage.getItem(propertyKey);
      
      if (!rawValue) {
        return null;
      }
  
      // Decodificar si está en Base64
      const decodedValue = this.isBase64(rawValue) 
        ? decodeURIComponent(escape(atob(rawValue))) 
        : rawValue;
  
      // Parsear si es JSON
      if (this.isJson(decodedValue)) {
        return JSON.parse(decodedValue) as T;
      }
  
      // Si no es JSON, retornar el string directamente
      return decodedValue;
  
    } catch (error) {
      console.error(`Error al obtener la propiedad "${propertyKey}":`, error);
      return null;
    }
  }

  /**
   * Obtiene propiedades específicas de un objeto almacenado previamente o el objeto completo si no se especifican propiedades.
   * 
   * @param {string} objStorage - La clave del objeto almacenado en el `sessionStorage`.
   * @param {Array<string>} properties - Arreglo de cadenas con los nombres de las propiedades a extraer.
   * @returns {any} Un objeto que contiene las propiedades solicitadas extraídas, el objeto original, o una cadena vacía en caso de no existir.
   */
  public getValueSt(objStorage: string, properties: Array<string>) {
    let objUsuIni: any = {},
      objCreate: any;
    if (typeof properties === "undefined" || properties.length === 0) {
      objCreate = sessionStorage[objStorage]
        ? this.getPropertie(objStorage)
        : "";
    } else {
      objCreate = {};
      objUsuIni = sessionStorage[objStorage]
        ? this.getPropertie(objStorage)
        : "";
      properties.map((valuePropertie) => {
        Object.keys(objUsuIni).map((value, key) => {
          if (value === valuePropertie) {
            objCreate[valuePropertie] = objUsuIni[value];
          }
        });
      });
    }

    return objCreate;
  }

  /**
   * Guarda o actualiza un dato en el `sessionStorage` utilizando una clave específica.
   * Si el valor proporcionado es de tipo objeto, lo convierte a un JSON string.
   * El valor resultante se guarda siempre codificado en Base64.
   * 
   * @param {string} itmName - El nombre o clave bajo el cual se guardará el valor.
   * @param {any} value - El valor que se almacenará en la sesión.
   * @returns {boolean} Retorna `true` al finalizar el almacenamiento de manera exitosa.
   */
  setItmStorage(itmName: string, value: any) {  
    if (typeof value === "object") {
      value = JSON.stringify(value);
    }
      sessionStorage.setItem(itmName, btoa(unescape(encodeURIComponent(value))));
    return true;
  }

  /**
   * Recupera un arreglo a partir de los datos almacenados bajo la clave especificada.
   * Extrae los elementos parseando el valor original (el cual pudo haber sido almacenado separado por `chartSeparator`).
   * 
   * @param {string} itmName - La clave en `sessionStorage` correspondiente a los datos.
   * @returns {Array<any>} Un arreglo con los elementos extraídos y decodificados.
   */
  getArrayFromStorage(itmName:string): any[] {
    let storageList = [],
      getStorageList = this.getPropertie(itmName);
    if (typeof getStorageList === "object") {
      storageList.push(getStorageList);
    } else {
      let containStorageList = getStorageList.split(this.chartSeparator);
      containStorageList.forEach((itm: string) => {
        storageList.push(JSON.parse(itm));
      });
    }
    return storageList;
  }

  /**
   * Agrega un nuevo elemento u objeto serializado dentro de un pseudo-arreglo guardado en el `sessionStorage`.
   * Si la clave aún no existe, utiliza `setItmStorage` para crearla. 
   * Si la clave ya existe, concatena el nuevo valor utilizando el separador `chartSeparator`.
   * 
   * @param {string} itmName - La clave de almacenamiento correspondiente al pseudo-arreglo.
   * @param {any} value - El nuevo valor a ser agregado.
   * @returns {void} No retorna ningún valor.
   */
  setArrayStorage(itmName: string, value: any) {
    let inStorage = sessionStorage.getItem(itmName);
    if (inStorage == undefined) {
      this.setItmStorage(itmName, value);
    } else {
      this.setItmStorage(
        itmName,
        inStorage + this.chartSeparator + JSON.stringify(value)
      );
    }
  }

  /**
   * Sobrescribe un valor específico dentro de un objeto almacenado o lo reemplaza en su totalidad.
   * 
   * Extrae el objeto actual, modifica su información, limpia la referencia en el storage,
   * y vuelve a guardarlo correctamente.
   * 
   * @param {Object} valueOverwrite - Configuración para la sobreescritura del almacenamiento.
   * @param {string} valueOverwrite.nameStorage - Nombre de la clave guardada actualmente en el storage.
   * @param {string} [valueOverwrite.propertie] - (Opcional) Propiedad del objeto almacenado que se desea sobreescribir.
   * @param {any} valueOverwrite.valNew - El nuevo valor que sobrescribirá el dato almacenado original.
   * @param {boolean} [complete=false] - Define si la sustitución es completa suprimiendo el original (`true`), o únicamente sobre escribiendo la propiedad indicada (`false`).
   * @returns {Promise<boolean>} Retorna una Promesa resolviendo a `true` si se actualizó con éxito, o rechazando a `false` si el objeto original no fue encontrado.
   * 
   * @example
   *  this._mStorage.overWritePropertie({
   *    nameStorage: 'selectedContract',
   *    propertie: 'saldoActual',
   *    valNew: this.valNewPropertie || { this.valNewObject } 
   *  }).then(() => { }, () => { console.error(vGlobal.errOverWrite) })
   */
  overWritePropertie(valueOverwrite: { nameStorage: string; propertie?: string; valNew: any }, complete = false) {

    let nameStorage = valueOverwrite.nameStorage,
      propertie = valueOverwrite.propertie || '',
      valNew = valueOverwrite.valNew,
      valBckUpInStorage = this.getPropertie(nameStorage);

    return new Promise<boolean>((rslvOvrwrite, rjctOvrwrite) => {

      if (valBckUpInStorage) {
        if (!complete) { valBckUpInStorage[propertie] = valNew; }
        else { valBckUpInStorage = valNew; }
       
        this.clearStorage(nameStorage)
        this.setItmStorage(nameStorage, valBckUpInStorage)
       
        rslvOvrwrite(true)
      } else { rjctOvrwrite(false) }

    })

  }

}